import {
  Button,
  Modal,
  Form,
  Input,
  Flex,
  Typography,
  message,
} from "antd";
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { EditOutlined, BankOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import UploadFile from "@/components/UplaodFileVideo";

const AddModal = forwardRef((props, ref) => {
  const { onSuccess } = props;
  const [form] = Form.useForm();
  const contractsRef = useRef();
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const resetModal = () => {
    setVisible(false);
    setUploadedFiles([]);
    contractsRef.current?.clearFileList?.();
    form.resetFields();
  };

  useImperativeHandle(ref, () => ({
    openModal: () => {
      setVisible(true);
      setUploadedFiles([]);
      form.resetFields();
    },
    setModelAdd: () => {
      setVisible(true);
      setUploadedFiles([]);
      form.resetFields();
    },
  }));

  const uploadFile = async (file, fieldType = "signature") => {
    const formData = new FormData();
    formData.append("files", file.originFileObj);
    formData.append("type", fieldType);

    const response = await http.post(api.getSignaTureUpload, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    if (response?.code === "1" && response?.data?.length) {
      const uploadedItem = response.data[0];
      return {
        uid: file.uid,
        name:
          uploadedItem?.fileName || uploadedItem?.name || file?.name || "",
        status: "done",
        path: uploadedItem?.path || "",
        fileName:
          uploadedItem?.fileName || uploadedItem?.name || file?.name || "",
        url: uploadedItem?.url || uploadedItem?.allPath || "",
        allPath: uploadedItem?.url || uploadedItem?.allPath || "",
        type: file?.type || uploadedItem?.contentType || "",
      };
    }

    throw new Error(response?.msg || "File upload failed.");
  };

  const handleUploadChange = async (files = []) => {
    try {
      setUploadLoading(true);

      const currentUploadedMap = new Map(
        uploadedFiles.map((item) => [String(item.uid), item]),
      );

      const nextFiles = [];
      for (const file of files) {
        const fileKey = String(file.uid);
        const existingFile = currentUploadedMap.get(fileKey);

        if (existingFile) {
          nextFiles.push(existingFile);
          continue;
        }

        if (file?.originFileObj) {
          const uploadedFile = await uploadFile(file, "signature");
          nextFiles.push(uploadedFile);
        }
      }

      setUploadedFiles(nextFiles);
      form.setFieldValue(
        "fileList",
        nextFiles.map((item) => ({
          path: item?.path || "",
          fileName: item?.fileName || item?.name || "",
          url: item?.url || item?.allPath || "",
        })),
      );
    } catch (error) {
      console.error(error);
      message.error(error?.message || "File upload failed.");
    } finally {
      setUploadLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);

      const queryParams = {
        account: values?.account,
        signature: values?.signature,
        remark: values?.remark,
        fileList: uploadedFiles.map((item) => ({
          path: item?.path || "",
          fileName: item?.fileName || item?.name || "",
          url: item?.url || item?.allPath || "",
        })),
      };

      const response = await http.post(api.getSignaTureAdd, queryParams);
      if (response?.code === "1") {
        message.success("Add successfully.");
        resetModal();
        onSuccess?.();
      } else {
        message.error(response?.msg || "Update failed");
      }
    } catch (error) {
      console.error(error);
      message.error(error?.message || "Submit an error and try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Flex justify="flex-start" align="flex-start" className="model_title">
          <EditOutlined className="model_title_mr10" />
          <Typography.Title level={4} className="mg">
            NEW SIGNATURE
          </Typography.Title>
        </Flex>
      }
      width="60%"
      open={visible}
      maskClosable={false}
      onCancel={resetModal}
      destroyOnClose
      footer={[
        <Button key="back" onClick={resetModal}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading || uploadLoading}
          disabled={loading || uploadLoading}
          onClick={() => form.submit()}
        >
          Submit
        </Button>,
      ]}
    >
      <Flex justify="space-between" align="flex-start">
        <div className="model_top" style={{ width: "100%" }}>
          <Form
            form={form}
            layout="horizontal"
            onFinish={onFinish}
            labelCol={{ style: { width: "160px" } }}
            wrapperCol={{ style: { flex: 1 } }}
          >
            <Flex align="flex-start" justify="space-between" gap={16}>
              <div style={{ flex: 0.9 }}>
                <div className="modal-section-title">
                  <BankOutlined /> Signature Detail
                </div>

                <Form.Item
                  name="account"
                  label="Account"
                  rules={[
                    {
                      required: true,
                      message: "Please enter account.",
                    },
                  ]}
                >
                  <Input placeholder="Please enter account." allowClear />
                </Form.Item>

                <Form.Item name="signature" label="Signature">
                  <Input
                    placeholder="Please enter signature."
                    allowClear
                    maxLength={50}
                    showCount
                  />
                </Form.Item>

                <Form.Item name="remark" label="Remark">
                  <Input placeholder="Please enter remark." allowClear />
                </Form.Item>
              </div>

              <div style={{ flex: 1.1 }}>
                <div
                  style={{
                    marginBottom: "16px",
                    fontWeight: "bold",
                    fontSize: "20px",
                  }}
                >
                  Upload
                </div>

                <Form.Item name="pictureOrVideo" label="Picture or video">
                  <UploadFile
                    ref={contractsRef}
                    sendDataToParent={handleUploadChange}
                    accept=".pdf,.jpg,.png,.mp4"
                    multiple
                    defaultFileList={uploadedFiles}
                  />
                </Form.Item>
                <Form.Item name="fileList" hidden>
                  <Input />
                </Form.Item>
              </div>
            </Flex>
          </Form>
        </div>
      </Flex>
    </Modal>
  );
});

export default AddModal;
